import {
  getBootstrapData,
  useBootstrapDataStore,
} from '@ui/bootstrap-data/bootstrap-data-store';
import {useEffect} from 'react';
import {create} from 'zustand';

/**
 * UI translation lines per language (build 131).
 *
 * Lines are no longer embedded in the bootstrap payload. The active language
 * is loaded from a cached, versioned JSON (`i18n.lines_url`) that the HTML
 * starts fetching in <head> (`window.__cdI18nLines`), so the request runs in
 * parallel with the app bundle. A language whose lines all equal their keys
 * (English) has no URL and costs nothing.
 */
type Lines = Record<string, string>;

interface LocaleLinesState {
  lines: Record<string, Lines>;
  loadedUrls: Record<string, string>;
}

declare global {
  interface Window {
    __cdI18nLines?: Promise<Lines>;
  }
}

export const useLocaleLinesStore = create<LocaleLinesState>()(() => ({
  lines: {},
  loadedUrls: {},
}));

export function setLocaleLines(locale: string, lines: Lines, url = '') {
  useLocaleLinesStore.setState(state => ({
    lines: {...state.lines, [locale]: lines},
    loadedUrls: {...state.loadedUrls, [locale]: url},
  }));
}

const pending: Record<string, Promise<void>> = {};

export function loadLocaleLines(locale: string, url: string | null | undefined): Promise<void> {
  if (!url) {
    if (!useLocaleLinesStore.getState().lines[locale]) setLocaleLines(locale, {});
    return Promise.resolve();
  }
  if (useLocaleLinesStore.getState().loadedUrls[locale] === url) {
    return Promise.resolve();
  }
  const key = `${locale}|${url}`;
  pending[key] ??= fetch(url, {credentials: 'same-origin'})
    .then(r => (r.ok ? r.json() : {}))
    .catch(() => ({}))
    .then((lines: Lines) => setLocaleLines(locale, lines ?? {}, url))
    .finally(() => delete pending[key]);
  return pending[key];
}

/** Called once before the first render. */
export async function ensureActiveLocaleLines(): Promise<void> {
  const i18n = getBootstrapData()?.i18n;
  if (!i18n?.active) return;
  const url = i18n.lines_url;
  if (url && typeof window !== 'undefined' && window.__cdI18nLines) {
    try {
      setLocaleLines(i18n.active, (await window.__cdI18nLines) ?? {}, url);
      return;
    } catch {
      // fall back to a normal request below
    }
  }
  await loadLocaleLines(i18n.active, url);
}

/**
 * Keeps the active language's lines loaded when bootstrap data changes after
 * the first render (login, workspace switch, …).
 */
export function LocaleLinesSync() {
  const active = useBootstrapDataStore(s => s.data?.i18n?.active);
  const url = useBootstrapDataStore(s => s.data?.i18n?.lines_url);
  useEffect(() => {
    if (active) void loadLocaleLines(active, url);
  }, [active, url]);
  return null;
}
